import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import useProvinciaData from "../../../hooks/administrador/general/tarjetas/useProvinciaData";

const SolicitudesProvinciaBarChart = ({ year, mes, token, idAdministrador }) => {
  const { data, loading } = useProvinciaData(year, mes, token, idAdministrador);
  const chartRef = useRef(null);
  let chartInstance = null;

  const colorPalette = [
    "#d1dc5a",
    "#e0f7e0",
    "#6ac5cb",
    "#45444e",
    "#a3b85b",
    "#3a6b74",
  ];
  
  const truncateName = (name) => {
    if (name.length > 13) {
      return name.substring(0, 13) + "...";
    }
    return name;
  };

  const updateChart = () => {
    if (!chartRef.current || !data || data.length === 0) return;
    
    if (!chartInstance) {
      chartInstance = echarts.init(chartRef.current);
    }
    
    const sortedData = [...data].sort((a, b) => b.Solicitudes_realizadas - a.Solicitudes_realizadas);
    
    const top10Data = sortedData.slice(0, 10);
    
    const otherProvinces = sortedData.slice(10);
    let otherSolicitudes = 0;
    let otherCantidad = 0;
    let otherPeso = 0;
    
    if (otherProvinces.length > 0) {
      otherSolicitudes = otherProvinces.reduce((sum, item) => sum + item.Solicitudes_realizadas, 0);
      otherCantidad = otherProvinces.reduce((sum, item) => sum + (parseFloat(item.cantidad_unidades) || 0), 0);
      otherPeso = otherProvinces.reduce((sum, item) => sum + (parseFloat(item.Peso_toneladas) || 0), 0);
      
      top10Data.push({
        Provincia: "Otros",
        Solicitudes_realizadas: otherSolicitudes,
        cantidad_unidades: otherCantidad.toString(),
        Peso_toneladas: otherPeso.toFixed(2)
      });
    }
    
    const displayData = [...top10Data].reverse();
    
    const provincias = displayData.map(item => truncateName(item.Provincia));
    const solicitudes = displayData.map(item => item.Solicitudes_realizadas);
    
    const seriesData = solicitudes.map((value, index) => ({
      value,
      itemStyle: {
        color: colorPalette[index % colorPalette.length]
      }
    }));

    const totalSolicitudes = data.reduce((sum, item) => sum + item.Solicitudes_realizadas, 0);
    const totalCantidad = data.reduce((sum, item) => {
      const cantidad = item.cantidad_unidades ? parseFloat(item.cantidad_unidades.replace(/,/g, '')) : 0;
      return sum + (isNaN(cantidad) ? 0 : cantidad);
    }, 0);
    
    const totalPeso = data.reduce((sum, item) => {
      const peso = item.Peso_toneladas ? parseFloat(item.Peso_toneladas.replace(/,/g, '')) : 0;
      return sum + (isNaN(peso) ? 0 : peso);
    }, 0);

    
    const options = {
      title: {
        text: 'Gestión realizada por provincia',
        left: 'center',
        subtext: `TOTAL RECOLECTADOS: ${Math.round(totalCantidad).toLocaleString()} [U] | PESO: ${totalPeso.toFixed(2)} [Tn] \nSOLICITUDES: ${totalSolicitudes.toLocaleString()}`,
        subtextStyle: {
          fontWeight: 'bold',
          lineHeight: 16, 
          fontSize: 11,    
          padding: [0, 0, 0, 0] 
        },
        padding: [0, 0, 2, 0], 
        itemGap: 6 
      },
      
      color: colorPalette,
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params) {
          const dataIndex = displayData.length - 1 - params[0].dataIndex;
          const provinciaData = top10Data[dataIndex];
          return `
            <div style="padding: 5px;">
              <strong>Provincia:</strong> ${provinciaData.Provincia}<br/>
              <strong>Solicitudes:</strong> ${provinciaData.Solicitudes_realizadas}<br/>
              <strong>Cantidades:</strong> ${provinciaData.cantidad_unidades || 0} [U]<br/>
              <strong>Peso:</strong> ${provinciaData.Peso_toneladas || 0} [Tn]
            </div>
          `;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '60px', 
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: 'Solicitudes Realizadas',
        nameLocation: 'middle',
        nameGap: 30,
        axisLabel: {
          formatter: function(value) {
            if (value >= 1000) {
              return (value / 1000) + 'K';
            }
            return value;
          }
        }
      },
      yAxis: {
        type: 'category',
        data: provincias,
        axisLabel: {
          fontSize: 12,
          margin: 16
        }
      },
      series: [
        {
          name: 'Solicitudes',
          type: 'bar',
          data: seriesData
        }
      ]
    };

    chartInstance.setOption(options);
  };

  useEffect(() => {
    if (chartRef.current && !chartInstance) {
      chartInstance = echarts.init(chartRef.current);
    }
    
    if (!loading && data && data.length > 0) {
      updateChart();
    }

    return () => {
      if (chartInstance) {
        chartInstance.dispose();
        chartInstance = null;
      }
    };
  }, [data, loading]);

  useEffect(() => {
    const handleResize = () => {
      if (chartInstance) {
        chartInstance.resize();
      }
    };

    let resizeObserver;
    if (window.ResizeObserver && chartRef.current) {
      resizeObserver = new ResizeObserver(() => {
        if (chartInstance) {
          setTimeout(() => {
            chartInstance.resize();
            updateChart();
          }, 50);
        }
      });
      resizeObserver.observe(chartRef.current);
    }

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver && chartRef.current) {
        resizeObserver.unobserve(chartRef.current);
      }
    };
  }, [data]);

  if (loading) {
    return <div className="loading-container">Cargando datos...</div>;
  }

  if (!data || !data.length) {
    return <div className="no-data-container">No hay datos disponibles</div>;
  }

  return <div className="grafica" ref={chartRef} style={{ height: '500px', width: '100%' }}></div>;
};

export default SolicitudesProvinciaBarChart;